import { api } from './client';
import type { Album, AlbumDraftChoices, AlbumDraftOptions, AlbumFiguresMap, AlbumTemplate, AlbumTextAlign, AlbumTextSize, AlbumVeil, AlbumView } from '../types';

export interface AlbumPageLook {
  background?: string | null;
  backgroundPhotoId?: string | null;
  backgroundAlbumPhotoId?: string;
  backgroundVeil?: AlbumVeil;
}

/** ADR-0046: always the viewer's own album — the server never takes a userId. */
export const albumApi = {
  get: (tripId: string) =>
    api.get<AlbumView>(`/trips/${tripId}/album`).then(r => r.data),
  /** ADR-0050: the wizard's choices shape the draft. */
  create: (tripId: string, choices: AlbumDraftChoices = {}) =>
    api.post<Album>(`/trips/${tripId}/album`, choices).then(r => r.data),
  draftOptions: (tripId: string) =>
    api.get<AlbumDraftOptions>(`/trips/${tripId}/album/draft-options`).then(r => r.data),
  /** A new draft in place of every page; the album's own photos stay (ADR-0051). */
  restart: (tripId: string, choices: AlbumDraftChoices) =>
    api.post<Album>(`/trips/${tripId}/album/restart`, choices).then(r => r.data),
  /** ADR-0050: replaces only the pages the draft made for that day. */
  redoDay: (tripId: string, dayId: string) =>
    api.post<Album>(`/trips/${tripId}/album/days/${dayId}/redo`).then(r => r.data),
  /** ADR-0051: a photo for this album only — never the trip's gallery. */
  uploadPhoto: (tripId: string, file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<Album>(`/trips/${tripId}/album/photos`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: e => onProgress?.(Math.round((e.loaded / (e.total ?? e.loaded)) * 100)),
    }).then(r => r.data);
  },
  removePhoto: (tripId: string, photoId: string) =>
    api.delete<Album>(`/trips/${tripId}/album/photos/${photoId}`).then(r => r.data),
  /** ADR-0049: re-read the view after it — the page and every frame change. */
  setFormat: (tripId: string, format: string) =>
    api.patch<Album>(`/trips/${tripId}/album`, { format }).then(r => r.data),
  /** ADR-0048: the album's typeface or photo frame — ids of the catalogue. */
  setAlbumStyle: (tripId: string, style: { fontPair?: string; photoFrame?: string }) =>
    api.patch<Album>(`/trips/${tripId}/album`, style).then(r => r.data),
  setTheme: (tripId: string, theme: string) =>
    api.patch<Album>(`/trips/${tripId}/album`, { theme }).then(r => r.data),
  /** ADR-0048: `background: null` goes back to the theme's. */
  /** A photo of either kind clears the other; `backgroundPhotoId: null` removes both. */
  setPageLook: (tripId: string, pageId: string, look: AlbumPageLook) =>
    api.patch<Album>(`/trips/${tripId}/album/pages/${pageId}`, look).then(r => r.data),
  remove: (tripId: string) =>
    api.delete(`/trips/${tripId}/album`),
  /** ADR-0052: two pages with the template, or one panorama; `position` among the inner spreads. */
  addSpread: (tripId: string, template: AlbumTemplate, position?: number) =>
    api.post<Album>(`/trips/${tripId}/album/spreads`, { template, position }).then(r => r.data),
  /** Every inner spread's id, in the new order: cover, opening and closing never move. */
  reorderSpreads: (tripId: string, spreadIds: string[]) =>
    api.patch<Album>(`/trips/${tripId}/album/spreads/reorder`, { spreadIds }).then(r => r.data),
  removeSpread: (tripId: string, spreadId: string) =>
    api.delete<Album>(`/trips/${tripId}/album/spreads/${spreadId}`).then(r => r.data),
  swapSides: (tripId: string, spreadId: string) =>
    api.post<Album>(`/trips/${tripId}/album/spreads/${spreadId}/swap`).then(r => r.data),
  setSpineText: (tripId: string, spineText: string | null) =>
    api.patch<Album>(`/trips/${tripId}/album`, { spineText }).then(r => r.data),
  changeTemplate: (tripId: string, pageId: string, template: AlbumTemplate) =>
    api.patch<Album>(`/trips/${tripId}/album/pages/${pageId}`, { template }).then(r => r.data),
  setFiguresMap: (tripId: string, pageId: string, figuresMap: AlbumFiguresMap) =>
    api.patch<Album>(`/trips/${tripId}/album/pages/${pageId}`, { figuresMap }).then(r => r.data),
  setFigures: (tripId: string, pageId: string, figures: { figuresRoute?: boolean; figuresOutline?: boolean; mapLegend?: boolean }) =>
    api.patch<Album>(`/trips/${tripId}/album/pages/${pageId}`, figures).then(r => r.data),
  setSlot: (tripId: string, pageId: string, index: number, content: { photoId: string | null } | { albumPhotoId: string } | { text: string | null } | { dayId: string | null } | { photoX: number; photoY: number } | { textSize?: AlbumTextSize | null; textPt?: number | null; textAlign?: AlbumTextAlign | null; font?: string | null }) =>
    api.patch<Album>(`/trips/${tripId}/album/pages/${pageId}/slots/${index}`, content).then(r => r.data),
};
